import { z } from 'zod'

import { pagedSchema } from '../core'

/** A collection: a named folder of an account, where its templates are kept. */
export const collectionSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  name: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
})
export type CollectionDto = z.infer<typeof collectionSchema>

export const collectionPageSchema = pagedSchema(collectionSchema)

/** POST /api/collections. */
export interface CollectionCreateBody {
  accountId: string
  createdBy: string
  name: string
}

/** PUT /api/collections/{id}: the name is all that can change. */
export interface CollectionUpdateBody {
  name: string
}

export interface CollectionFilters {
  /** The account whose collections to list. */
  accountId?: string
}

/** One template file kept in a collection. `fileUrl` is a storage path, not a web address. */
export const collectionAssetSchema = z.object({
  id: z.string(),
  collectionId: z.string().nullish(),
  assetType: z.string().nullish(),
  fileName: z.string().nullish(),
  fileFormat: z.string().nullish(),
  fileSize: z.number().nullish().transform((size) => size ?? 0),
  /** The storage paths of the file's pictures, one for each page (a one-page file has one). Older answers sent a single text: it is read as a list of one. */
  fileUrl: z.union([z.array(z.string()), z.string()]).nullish().transform((value) => (Array.isArray(value) ? value : value ? [value] : [])),
  createdAt: z.string().nullish(),
})
export type CollectionAssetDto = z.infer<typeof collectionAssetSchema>

export const collectionAssetPageSchema = pagedSchema(collectionAssetSchema)

/** What POST /api/collections/{id}/assets answers: the files that were kept, and a message for each that was not. */
/** A file the server refused, and why. */
const refusedSchema = z.union([z.string(), z.object({ fileName: z.string().nullish(), error: z.string().nullish(), message: z.string().nullish() }).passthrough()])

/** What POST /api/collections/{id}/assets answers: the files that were kept, and a message for each that was not (as "name: reason"). */
export const collectionUploadSchema = z.object({
  collectionId: z.string(),
  assets: z.array(collectionAssetSchema).nullish().transform((assets) => assets ?? []),
  errors: z.array(refusedSchema).nullish().transform((errors) => (errors ?? []).map((item) => (typeof item === 'string' ? item : [item.fileName, item.error ?? item.message].filter(Boolean).join(': ') || 'A file could not be added.'))),
})
export type CollectionUploadDto = z.infer<typeof collectionUploadSchema>
